import { CardContainer } from "@/components/commons/CardContainer";
import { ItemInfo } from "@/components/commons/ItemInfo";
import { useEnum } from "@/api/services/global/enum";
import { STATUS_LABEL } from "@/constants/enum";
import { TSubjectDetailInfo } from "@/features/learning-content/types/learningContent";
import { IconButton, InputAdornment, OutlinedInput, Stack, Tooltip, Typography, styled } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

type TSubjectInfoCardProps = {
  info: TSubjectDetailInfo;
  onMemoBlur?: (value: string) => void;
  isSavingMemo?: boolean;
};

// Displays basic subject information in a card layout matching the design
export const SubjectInfoCard: FC<TSubjectInfoCardProps> = ({ info, onMemoBlur, isSavingMemo }) => {
  const { data: partTypeOptions } = useEnum("EPartType");
  const [noteValue, setNoteValue] = useState(info.internalNote ?? "");

  // Resolve パート enum label from value
  const partTypeLabel = useMemo(() => {
    if (!info.subjectCode) return "----";
    return partTypeOptions.find((opt) => opt.value === info.subjectCode)?.label ?? info.subjectCode;
  }, [info.subjectCode, partTypeOptions]);

  // Sync local state when internalNote changes from parent (e.g., after API fetch)
  useEffect(() => {
    setNoteValue(info.internalNote ?? "");
  }, [info.internalNote]);

  const handleClear = () => {
    setNoteValue("");
  };

  const handleBlur = () => {
    if (noteValue.trim() !== (info.internalNote ?? "").trim()) {
      onMemoBlur?.(noteValue);
    }
  };

  return (
    <CardContainer sx={{ borderRadius: "8px" }}>
      <Stack gap={3}>
        <Typography variant="sub16Bold">WEEK情報</Typography>

        {/* Row 1: WEEK名, パート, ステータス, 有効化日 */}
        <InfoRow>
          <InfoItem $flex={2}>
            <ItemInfo label="WEEK名" value={info.subjectName} />
          </InfoItem>
          <InfoItem>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <InfoLabel>パート</InfoLabel>
              <Tooltip title="パート区分">
                <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary", cursor: "pointer" }} />
              </Tooltip>
            </Stack>
            <ItemInfo value={partTypeLabel} />
          </InfoItem>
          <InfoItem>
            <ItemInfo label="ステータス" value={info.status === "enabled" ? "有効" : STATUS_LABEL.DISABLED} />
          </InfoItem>
          <InfoItem>
            <ItemInfo label="有効化日" value={info.activationDate} />
          </InfoItem>
        </InfoRow>

        {/* Row 2: 社内メモ */}
        <Stack gap={1}>
          <InfoLabel>社内メモ</InfoLabel>
          <OutlinedInput
            fullWidth
            size="small"
            value={noteValue}
            onChange={(e) => setNoteValue(e.target.value)}
            onBlur={handleBlur}
            disabled={isSavingMemo}
            placeholder="社内メモを入力"
            endAdornment={
              noteValue ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClear} edge="end">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
            sx={{
              bgcolor: "background.paper",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "divider",
              },
            }}
          />
        </Stack>
      </Stack>
    </CardContainer>
  );
};

// Styled components for consistent layout
const InfoRow = styled(Stack)`
  flex-direction: row;
  gap: 24px;
`;

const InfoItem = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "$flex",
})<{ $flex?: number }>(({ $flex }) => ({
  flex: $flex ?? 1,
  gap: "4px",
}));

const InfoLabel = styled(Typography)`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;
